import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import multiMonthPlugin from "@fullcalendar/multimonth"
import timeGridPlugin from "@fullcalendar/timegrid"
import listPlugin from "@fullcalendar/list"
import interactionPlugin from "@fullcalendar/interaction"
import { Turbo } from "@hotwired/turbo-rails"
import { I18n } from "i18n-js"
import locales from "../locales.json"

const STORAGE_KEY = "tshmanager_calendar_filters"

// Fallback color map — primary source of truth is data-color on status chips in the view
const STATUS_COLORS_FALLBACK = {
  open:          "#0d6efd",
  inprogress:    "#fd7e14",
  done:          "#198754",
  closed:        "#6c757d",
  not_fulfilled: "#dc3545",
  reject:        "#dc3545",
  scheduled:     "#6f42c1"
}

export default class extends Controller {
  static values = {
    eventsUrl: String,
    icalUrl: String,
    locale: { type: String, default: "hu" }
  }

  static targets = [
    "scopeToggle",
    // Building complex
    "buildingComplexSelect",
    "buildingComplexDropdownBtn",
    "buildingComplexLabel",
    "buildingComplexOption",
    // Type chips
    "typeChip",
    "typeSelect",
    // Status chips
    "statusChip",
    "statusSelect",
    // Loader
    "loaderOverlay",
    // Event popup
    "eventPopup",
    // Filter collapse
    "filterBar",
    "filtersBody",
    "filtersChevron"
  ]

  connect() {
    this.calendar = null
    this._selectedBuildingComplexId = ""
    this._filtersOpen = true
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || this.localeValue
    this.loadFilters()
    this.initCalendar()
    this.updateIcalLink()
    this.scrollToToday()
    this._setupScrollCollapse()
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
    this.hideEventPopup()
    this._teardownScrollCollapse()
  }

  // ── Event popup ──────────────────────────────────────────────────

  showEventPopup(event, jsEvent) {
    // Remove any existing outside handler before showing new popup
    this._removePopupOutsideHandler()

    const props = event.extendedProps
    const color = event.backgroundColor || event.color || "#0d6efd"
    const popup = this.eventPopupTarget

    popup.innerHTML = `
      <div class="calendar-popup-header" style="background: ${color};">
        <span class="calendar-popup-title">${this.escHtml(event.title)}</span>
        <button type="button" class="calendar-popup-close" data-action="click->calendar#hideEventPopup" aria-label="Bezár">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="calendar-popup-body">
        ${props.status_label ? `<div class="calendar-popup-row">
          <span class="calendar-popup-badge" style="background:${color};">${this.escHtml(props.status_label)}</span>
        </div>` : ""}
        ${props.type_name ? `<div class="calendar-popup-row">
          <i class="bi bi-tag text-muted"></i>
          <span>${this.escHtml(props.type_name)}</span>
        </div>` : ""}
        ${props.building_complex_name ? `<div class="calendar-popup-row">
          <i class="bi bi-building text-muted"></i>
          <span>${this.escHtml(props.building_complex_name)}</span>
        </div>` : ""}
        ${props.responsible_name ? `<div class="calendar-popup-row">
          <i class="bi bi-person text-muted"></i>
          <span>${this.escHtml(props.responsible_name)}</span>
        </div>` : ""}
        ${event.start ? `<div class="calendar-popup-row">
          <i class="bi bi-calendar-event text-muted"></i>
          <span>${new Intl.DateTimeFormat(this.localeValue, { year: "numeric", month: "long", day: "numeric" }).format(event.start)}</span>
        </div>` : ""}
        <div class="calendar-popup-actions">
          <a href="${event.url}" class="btn btn-sm text-white" style="background:${color}; border-color:${color};">
            <i class="bi bi-arrow-right-circle me-1"></i>${this.i18n.t("tasks.calendar.open_task")}
          </a>
        </div>
      </div>
    `

    popup.classList.remove("d-none")
    popup.style.left = "0"
    popup.style.top = "0"
    popup.style.maxWidth = ""

    // After layout: measure real size and clamp fully inside the visual viewport
    requestAnimationFrame(() => {
      this._clampEventPopupToViewport(popup, jsEvent)
    })

    // Register outside-click handler on next tick so the current click doesn't immediately close it
    this._popupOutsideHandler = (e) => {
      if (!popup.contains(e.target)) this.hideEventPopup()
    }
    setTimeout(() => {
      // Only add if popup is still open
      if (!popup.classList.contains("d-none")) {
        document.addEventListener("click", this._popupOutsideHandler)
      }
    }, 50)
  }

  _clampEventPopupToViewport(popup, jsEvent) {
    const margin = 8
    const iw = window.innerWidth
    const ih = window.innerHeight
    const maxW = iw - 2 * margin

    if (popup.offsetWidth > maxW) {
      popup.style.maxWidth = `${maxW}px`
    }

    let pw = popup.offsetWidth
    let ph = popup.offsetHeight
    if (pw < 10) pw = 260
    if (ph < 10) ph = 200

    let x = jsEvent.clientX + margin
    let y = jsEvent.clientY + margin

    if (x + pw > iw - margin) x = jsEvent.clientX - pw - margin
    if (y + ph > ih - margin) y = jsEvent.clientY - ph - margin

    x = Math.min(Math.max(x, margin), Math.max(margin, iw - pw - margin))
    y = Math.min(Math.max(y, margin), Math.max(margin, ih - ph - margin))

    popup.style.left = `${Math.round(x)}px`
    popup.style.top = `${Math.round(y)}px`
  }

  hideEventPopup() {
    if (this.hasEventPopupTarget) this.eventPopupTarget.classList.add("d-none")
    this._removePopupOutsideHandler()
  }

  _removePopupOutsideHandler() {
    if (this._popupOutsideHandler) {
      document.removeEventListener("click", this._popupOutsideHandler)
      this._popupOutsideHandler = null
    }
  }

  escHtml(str) {
    return String(str ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;")
  }

  // ── Filter collapse ──────────────────────────────────────────────

  toggleFilters(event) {
    event.stopPropagation()
    event.preventDefault()
    if (this._filtersOpen) {
      this._collapseFilters()
    } else {
      this._expandFilters()
    }
  }

  _expandFilters() {
    this._filtersOpen = true
    this._ignoreNextScroll = true
    if (this.hasFiltersBodyTarget) this.filtersBodyTarget.classList.remove("collapsed")
    if (this.hasFiltersChevronTarget) this.filtersChevronTarget.classList.remove("rotated")
    // Reset ignore flag after layout settles
    setTimeout(() => { this._ignoreNextScroll = false }, 200)
  }

  _collapseFilters() {
    this._filtersOpen = false
    this._ignoreNextScroll = true
    if (this.hasFiltersBodyTarget) this.filtersBodyTarget.classList.add("collapsed")
    if (this.hasFiltersChevronTarget) this.filtersChevronTarget.classList.add("rotated")
    setTimeout(() => { this._ignoreNextScroll = false }, 200)
  }

  _setupScrollCollapse() {
    const container = document.getElementById("content-frame")
    if (!container) return
    let lastScrollTop = container.scrollTop
    this._scrollHandler = () => {
      if (this._ignoreNextScroll) return
      const st = container.scrollTop
      if (st > lastScrollTop && st > 60 && this._filtersOpen) {
        // Scrolling down → collapse
        this._collapseFilters()
      } else if (st < lastScrollTop && st < 40 && !this._filtersOpen) {
        // Scrolled back near top → expand
        this._expandFilters()
      }
      lastScrollTop = st
    }
    container.addEventListener("scroll", this._scrollHandler, { passive: true })
  }

  _teardownScrollCollapse() {
    const container = document.getElementById("content-frame")
    if (container && this._scrollHandler) {
      container.removeEventListener("scroll", this._scrollHandler)
    }
  }

  // ── Event handlers ──────────────────────────────────────────────

  onScopeChange() {
    this.saveFilters()
    this.calendar.refetchEvents()
  }

  onFilterChange() {
    this.saveFilters()
    this.calendar.refetchEvents()
  }

  onBuildingComplexSelect(event) {
    const btn = event.currentTarget
    const value = btn.dataset.value || ""
    this.syncBuildingComplexOptionsUI(value)

    // Bootstrap closes the dropdown automatically via data-bs-auto-close="true"

    this.saveFilters()
    this.calendar.refetchEvents()
  }

  /** Keeps label, .active, and check icons in sync (also after localStorage load). */
  syncBuildingComplexOptionsUI(rawValue) {
    const value = rawValue === undefined || rawValue === null ? "" : String(rawValue)
    this._selectedBuildingComplexId = value

    const matchingOpt = this.buildingComplexOptionTargets.find(
      o => String(o.dataset.value || "") === value
    )

    if (this.hasBuildingComplexLabelTarget) {
      if (matchingOpt) {
        this.buildingComplexLabelTarget.textContent =
          value ? (matchingOpt.dataset.label || matchingOpt.textContent.trim()) : matchingOpt.textContent.trim()
      }
    }

    this.buildingComplexOptionTargets.forEach(opt => {
      const isActive = String(opt.dataset.value || "") === value
      opt.classList.toggle("active", isActive)
      const icon = opt.querySelector(".bi-check2")
      if (icon) icon.style.visibility = isActive ? "visible" : "hidden"
    })
  }

  onTypeChipToggle(event) {
    const chip = event.currentTarget
    chip.classList.toggle("active")
    this.syncChipsToSelect("typeChip", "typeSelect")
    this.saveFilters()
    this.calendar.refetchEvents()
  }

  onStatusChipToggle(event) {
    const chip = event.currentTarget
    chip.classList.toggle("active")
    this.syncChipsToSelect("statusChip", "statusSelect")
    this.saveFilters()
    this.calendar.refetchEvents()
  }

  // ── Calendar init ────────────────────────────────────────────────

  initCalendar() {
    const calendarEl = this.element.querySelector("#fullcalendar")
    if (!calendarEl) return

    const isNarrowScreen = () => window.matchMedia("(max-width: 575.98px)").matches

    this.calendar = new Calendar(calendarEl, {
      plugins: [dayGridPlugin, multiMonthPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      initialView: "multiMonthYear",
      locale: this.localeValue,
      customButtons: {
        goToday: {
          text: this.i18n.t("tasks.calendar.btn_today"),
          click: () => {
            this.calendar.today()
            this.scrollToToday()
          }
        }
      },
      headerToolbar: {
        left: "prev,next goToday",
        center: "title",
        right: "multiMonthYear,dayGridWeek,listMultiYear"
      },
      buttonText: {
        multiMonthYear: this.i18n.t("tasks.calendar.btn_year"),
        week: this.i18n.t("tasks.calendar.btn_week"),
        listMultiYear: this.i18n.t("tasks.calendar.btn_list")
      },
      multiMonthMaxColumns: 1,
      // Narrow screens: show more stacked events before "+more", shorter link label
      dayMaxEvents: isNarrowScreen() ? 6 : true,
      moreLinkText: (n) =>
        isNarrowScreen()
          ? this.i18n.t("tasks.calendar.more_link_compact", { n })
          : this.i18n.t("tasks.calendar.more_link_text", { n }),
      views: {
        dayGridWeek: {
          dayHeaderFormat: { month: "short", day: "numeric" }
        },
        listMultiYear: {
          type: "list",
          visibleRange: (currentDate) => {
            return {
              start: new Date(currentDate.getFullYear() - 1, 0, 1),
              end: new Date(currentDate.getFullYear() + 2, 0, 1)
            }
          }
        }
      },
      height: "auto",
      firstDay: 1,
      events: (fetchInfo, successCallback, failureCallback) => {
        this.fetchEvents(fetchInfo, successCallback, failureCallback)
      },
      eventClick: (info) => {
        info.jsEvent.preventDefault()
        this.showEventPopup(info.event, info.jsEvent)
      },
      noEventsContent: this.i18n.t("tasks.calendar.no_events")
    })

    this.calendar.render()
  }

  scrollToToday() {
    let attempts = 0
    const tryScroll = () => {
      // list view uses fc-list-day-today; grid/multiMonth views use fc-day-today
      const todayEl =
        this.element.querySelector(".fc-list-day-today") ||
        this.element.querySelector(".fc-day-today")

      if (!todayEl) {
        if (attempts++ < 20) setTimeout(tryScroll, 150)
        return
      }

      const container = document.getElementById("content-frame")
      if (!container) return

      // getBoundingClientRect gives position relative to viewport
      const containerRect = container.getBoundingClientRect()
      const todayRect = todayEl.getBoundingClientRect()

      // Current scroll position of container
      const currentScroll = container.scrollTop

      // Position of todayEl relative to container top (accounting for current scroll)
      const todayRelTop = todayRect.top - containerRect.top + currentScroll

      // Center it vertically
      const targetScroll = todayRelTop - (container.clientHeight / 2) + (todayRect.height / 2)

      container.scrollTo({ top: Math.max(0, targetScroll), behavior: "smooth" })
    }
    setTimeout(tryScroll, 400)
  }

  // ── Data fetching ────────────────────────────────────────────────

  fetchEvents(fetchInfo, successCallback, failureCallback) {
    this.setLoading(true)

    const url = new URL(this.eventsUrlValue, window.location.origin)

    url.searchParams.set("scope", this.currentScope())

    if (this._selectedBuildingComplexId) {
      url.searchParams.set("building_complex_id", this._selectedBuildingComplexId)
    }

    // Active type chips → params. Send "__none__" sentinel if all deselected so backend returns nothing.
    const activeTypes = this.activeChipValues("typeChip")
    if (this.typeChipTargets.length > 0) {
      if (activeTypes.length > 0) {
        activeTypes.forEach(v => url.searchParams.append("type_id[]", v))
      } else {
        url.searchParams.append("type_id[]", "__none__")
      }
    }

    // Active status chips → params. Send "__none__" sentinel if all deselected.
    const activeStatuses = this.activeChipValues("statusChip")
    if (this.statusChipTargets.length > 0) {
      if (activeStatuses.length > 0) {
        activeStatuses.forEach(v => url.searchParams.append("status[]", v))
      } else {
        url.searchParams.append("status[]", "__none__")
      }
    }

    fetch(url.toString(), {
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then(response => {
        if (!response.ok) throw new Error("Network error")
        return response.json()
      })
      .then(data => {
        // Override event colors from our single source of truth (chip data-color attrs)
        const colored = data.map(event => ({
          ...event,
          color: this.colorForStatus(event.extendedProps?.status)
        }))
        successCallback(colored)
      })
      .catch(err => failureCallback(err))
      .finally(() => this.setLoading(false))
  }

  // ── Helpers ──────────────────────────────────────────────────────

  setLoading(isLoading) {
    if (!this.hasLoaderOverlayTarget) return
    this.loaderOverlayTarget.classList.toggle("d-none", !isLoading)
  }

  // Build color map from data-color attributes on status chips (single source of truth)
  get statusColorMap() {
    if (this._statusColorMap) return this._statusColorMap
    this._statusColorMap = { ...STATUS_COLORS_FALLBACK }
    this.statusChipTargets.forEach(chip => {
      if (chip.dataset.value && chip.dataset.color) {
        this._statusColorMap[chip.dataset.value] = chip.dataset.color
      }
    })
    return this._statusColorMap
  }

  colorForStatus(status) {
    return this.statusColorMap[status] || STATUS_COLORS_FALLBACK.open
  }

  currentScope() {
    if (this.hasScopeToggleTarget) {
      return this.scopeToggleTarget.checked ? "all" : "my"
    }
    return "my"
  }

  activeChipValues(targetName) {
    const targets = this[`${targetName}Targets`]
    if (!targets) return []
    return targets.filter(c => c.classList.contains("active")).map(c => c.dataset.value)
  }

  syncChipsToSelect(chipTargetName, selectTargetName) {
    const selectTarget = this[`${selectTargetName}Target`]
    if (!selectTarget) return
    const activeValues = this.activeChipValues(chipTargetName)
    Array.from(selectTarget.options).forEach(opt => {
      opt.selected = activeValues.includes(opt.value)
    })
  }

  // ── Persistence ──────────────────────────────────────────────────

  loadFilters() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}")

      if (this.hasScopeToggleTarget && saved.scope !== undefined) {
        this.scopeToggleTarget.checked = saved.scope === "all"
      }

      if (saved.building_complex_id !== undefined) {
        this.syncBuildingComplexOptionsUI(saved.building_complex_id)
      }

      // If saved type_ids exist, apply them; otherwise keep HTML defaults (all active)
      if (saved.type_ids !== undefined) {
        this.typeChipTargets.forEach(chip => {
          chip.classList.toggle("active", saved.type_ids.includes(chip.dataset.value))
        })
      }
      this.syncChipsToSelect("typeChip", "typeSelect")

      // If saved statuses exist, apply them; otherwise keep HTML defaults (open/inprogress/scheduled)
      if (saved.statuses !== undefined) {
        this.statusChipTargets.forEach(chip => {
          chip.classList.toggle("active", saved.statuses.includes(chip.dataset.value))
        })
      }
      this.syncChipsToSelect("statusChip", "statusSelect")
    } catch (_e) {
      // ignore parse errors
    }
  }

  saveFilters() {
    const filters = {
      scope: this.currentScope(),
      building_complex_id: this._selectedBuildingComplexId || "",
      type_ids: this.activeChipValues("typeChip"),
      statuses: this.activeChipValues("statusChip")
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
    this.updateIcalLink()
  }

  updateIcalLink() {
    const el = document.getElementById("calendar-ical-link")
    if (!el) return

    const url = new URL(this.icalUrlValue, window.location.origin)
    url.searchParams.set("scope", this.currentScope())

    if (this._selectedBuildingComplexId) {
      url.searchParams.set("building_complex_id", this._selectedBuildingComplexId)
    }

    const activeTypes = this.activeChipValues("typeChip")
    if (this.typeChipTargets.length > 0) {
      if (activeTypes.length > 0) {
        activeTypes.forEach(v => url.searchParams.append("type_id[]", v))
      } else {
        url.searchParams.append("type_id[]", "__none__")
      }
    }

    const activeStatuses = this.activeChipValues("statusChip")
    if (this.statusChipTargets.length > 0) {
      if (activeStatuses.length > 0) {
        activeStatuses.forEach(v => url.searchParams.append("status[]", v))
      } else {
        url.searchParams.append("status[]", "__none__")
      }
    }

    el.href = url.toString().replace(/^https?:\/\//, 'webcal://')
  }
}
